import { createAsyncThunk } from "@reduxjs/toolkit";
import client, {
  ApiError,
  ApiResponse,
  AsyncThunkConfig,
  isAxiosError,
} from "../../../api/axiosConfig";
import {
  FileData,
  PsychLabActivity,
  PsychLabUser,
  PsychologicalLab,
} from "../../../api/psychologicalLabApi";
import { dispatchNotification } from "../../../utils/redux";
import {
  psychologicalLabsActivityURL,
  psychologicalLabsURL,
  psychologicalLabsUserURL,
} from "../../../utils/paths";
import { selectLanguage } from "../../selectors/i18nSelectors";
import type { RootState } from "../../store";
import {
  FilterPsychLabActivitiesData,
  StatsPsychLabPayload,
} from "../../../components/Forms/types";
import { I18n } from "react-redux-i18n";

export const fetchSelectedPsychLabData = createAsyncThunk<
  PsychologicalLab,
  number,
  AsyncThunkConfig
>(
  "selectedPsychLab/fetchSelectedPsychLabData/get",
  async (psychLabId, thunkAPI) => {
    try {
      const response = await client.get<ApiResponse<PsychologicalLab>>(
        `${psychologicalLabsURL}${psychLabId}/`
      );

      return response.data.results;
    } catch (err) {
      if (isAxiosError(err) && err.response != null) {
        const extractedError = err.response.data as ApiError;

        dispatchNotification("error", extractedError.message);

        return thunkAPI.rejectWithValue(extractedError);
      }

      dispatchNotification("error");
      throw err;
    }
  }
);

export const fetchUsersList = createAsyncThunk<
  PsychLabUser[],
  number,
  AsyncThunkConfig
>("selectedPsychLab/fetchUsersList", async (psychLabId, thunkAPI) => {
  try {
    const response = await client.get<ApiResponse<PsychLabUser[]>>(
      psychologicalLabsUserURL,
      { params: { psychLabId } }
    );

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message);

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});

const serializeActivityListParams = (
  params: FilterPsychLabActivitiesData
): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else if (value instanceof Date) {
      searchParams.append(key, value.toISOString());
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

export const fetchActivityList = createAsyncThunk<
  PsychLabActivity[],
  FilterPsychLabActivitiesData,
  AsyncThunkConfig
>("selectedPsychLab/fetchActivityList", async (psychLabFilters, thunkAPI) => {
  try {
    const response = await client.get<ApiResponse<PsychLabActivity[]>>(
      `${psychologicalLabsActivityURL}filtered_list/`,
      {
        params: psychLabFilters,
        paramsSerializer: (params) =>
          serializeActivityListParams(params as FilterPsychLabActivitiesData),
      }
    );

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message);

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});

export const exportStats = createAsyncThunk<
  void,
  StatsPsychLabPayload,
  AsyncThunkConfig
>("selectedPsychLab/exportStats", async (payload, thunkAPI) => {
  try {
    await client.post<{ message: string }>(
      `${psychologicalLabsActivityURL}export_stats/`,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    return;
  } catch (err) {
    if (isAxiosError(err) && err.response?.data) {
      const errorData = err.response.data as ApiError;
      const errorMessage =
        errorData.message || "Wystąpił nieznany błąd podczas eksportu statystyk.";

      dispatchNotification("error", errorMessage, true);

      return thunkAPI.rejectWithValue(errorData);
    }

    throw err;
  }
});

interface StatsFilesApiResponse {
  results: {
    files: FileData[];
  };
}

export const fetchStatsFiles = createAsyncThunk<
  FileData[],
  void,
  AsyncThunkConfig
>("selectedPsychLab/fetchStatsFiles", async (_arg, thunkAPI) => {
  try {
    const response = await client.get<StatsFilesApiResponse>(
      `${psychologicalLabsActivityURL}stats_fetchfiles/`
    );

    return response.data.results.files;
  } catch (err) {
    let errorMessage = "Wystąpił błąd podczas pobierania listy plików.";
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (isAxiosError(err) && err.response?.data) {
      apiError = err.response.data as ApiError;
      errorMessage = apiError.message || errorMessage;
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});

export const downloadStatsFile = createAsyncThunk<
  void,
  string,
  AsyncThunkConfig
>("selectedPsychLab/downloadStatsFile", async (filename, thunkAPI) => {
  try {
    const response = await client.get(
      `${psychologicalLabsActivityURL}stats_download/${encodeURIComponent(filename)}/`,
      {
        responseType: "blob",
      }
    );

    if (typeof window !== "undefined") {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = /^(https?:\/\/|blob:)/.test(url) ? url : "#";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return;
  } catch (err) {
    let errorMessage = `Wystąpił błąd podczas pobierania pliku: ${filename}.`;
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (
      isAxiosError(err) &&
      err.response?.data instanceof Blob &&
      err.response.data.type === "application/json"
    ) {
      try {
        const errorJson = JSON.parse(
          await err.response.data.text()
        ) as ApiError;

        apiError = errorJson;
        errorMessage = apiError.message || errorMessage;
      } catch {
        // Failed to parse Blob error as JSON
      }
    } else if (isAxiosError(err) && err.response?.data) {
      if (
        typeof err.response.data === "object" &&
        !(err.response.data instanceof Blob)
      ) {
        apiError = err.response.data as ApiError;
        errorMessage = apiError.message || errorMessage;
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});

export const deleteStatsFile = createAsyncThunk<
  string,
  string,
  AsyncThunkConfig
>("selectedPsychLab/deleteStatsFile", async (filename, thunkAPI) => {
  try {
    await client.delete(
      `${psychologicalLabsActivityURL}stats_delete/${encodeURIComponent(filename)}/`
    );

    return filename;
  } catch (err) {
    let errorMessage = `Wystąpił błąd podczas usuwania pliku: ${filename}.`;
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (isAxiosError(err) && err.response?.data) {
      if (
        typeof err.response.data === "object" &&
        !(err.response.data instanceof Blob)
      ) {
        apiError = err.response.data as ApiError;
        errorMessage = apiError.message || errorMessage;
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});

// ============================================
// PARAMETERS ACTIONS
// ============================================

export interface PsychLabParameter {
  id?: number;
  param: number;
  paramName: string; // Backend zwraca camelCase przez FormattedCamelCaseJSONRenderer
  paramDesc: string;
  value: string;
  lab: number;
}

export interface AvailableParameterOption {
  value: string;
  label: string;
}

export interface AvailableParameter {
  // Renderer zwraca camelCase
  paramName: string;
  paramDesc: string;
  valueType: "boolean" | "multi_select" | "number";
  options?: AvailableParameterOption[];
}

export interface AvailableParametersResponse {
  // Custom renderer owrapuje w {results: {...}}
  results?: {
    // eslint-disable-next-line camelcase
    availableParameters?: AvailableParameter[];
    // eslint-disable-next-line camelcase
    available_parameters?: AvailableParameter[];
  };
  // Fallback dla bezpośredniego formatu
  // eslint-disable-next-line camelcase
  availableParameters?: AvailableParameter[];
  // eslint-disable-next-line camelcase
  available_parameters?: AvailableParameter[];
}

export interface BulkUpdateParametersRequest {
  // CamelCase parser konwertuje na snake_case dla backendu
  labId: number;
  parameters: Array<{
    paramName: string;
    value: string;
  }>;
}

export const fetchPsychLabParameters = createAsyncThunk<
  PsychLabParameter[],
  number,
  AsyncThunkConfig
>("selectedPsychLab/fetchParameters", async (labId, thunkAPI) => {
  try {
    const languageCode = selectLanguage(thunkAPI.getState() as RootState);
    const response = await client.get<ApiResponse<PsychLabParameter[]>>(
      "/api/users/psychlab_params/",
      {
        // eslint-disable-next-line camelcase
        params: { lab_id: labId },
        headers: { "Accept-Language": languageCode },
      }
    );

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message);

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});

export const fetchAvailableParameterOptions = createAsyncThunk<
  AvailableParametersResponse,
  number,
  AsyncThunkConfig
>("selectedPsychLab/fetchAvailableOptions", async (labId, thunkAPI) => {
  try {
    const languageCode = selectLanguage(thunkAPI.getState() as RootState);
    const response = await client.get<AvailableParametersResponse>(
      "/api/users/psychlab_params/available-options/",
      {
        // eslint-disable-next-line camelcase
        params: { lab_id: labId },
        headers: { "Accept-Language": languageCode },
      }
    );

    return response.data;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message);

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});

export const bulkUpdateParameters = createAsyncThunk<
  { message: string; created: number; updated: number; deleted: number },
  BulkUpdateParametersRequest,
  AsyncThunkConfig
>("selectedPsychLab/bulkUpdateParameters", async (data, thunkAPI) => {
  try {
    const response = await client.post<{
      message: string;
      created: number;
      updated: number;
      deleted: number;
    }>("/api/users/psychlab_params/bulk-update/", data);

    dispatchNotification(
      "success",
      I18n.t("Forms.adminSnackbar.parametersSaved")
    );

    return response.data;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message);

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});